const ProductShopSelector = require("../../../src/includes/shops/ProductShopSelector").default;
const React = require("react");
const { useParams, useNavigate } = require("react-router-dom");
const { useCJDropshipping } = require("../context/CJDropshippingContext");
const { useDropshippingResource } = require("../hooks/useDropshippingResource");
const { PageHeader, Button, LinkButton, ResourceState } = require("../components/ui");
const { CJVariantMultiSelector } = require("../components/cjShipping/CJVariantMultiSelector");
const { variantSelectionPayload, selectedVariantIds } = require("../cjShippingDestinations");
const h = React.createElement;

// Assistant de pré-import : le seller choisit un mode manuel (édition champ
// par champ) ou automatique (marge % + conversion devise + filtrage des
// variantes indisponibles appliqués par défaut) avant de créer le produit
// Elakay. La catégorie Elakay elle-même se choisit après import, via
// l'onglet "Categories" déjà existant dans editProduct (pas dupliqué ici).
const DropshippingImportWizard = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { api, t } = useCJDropshipping();
  const resource = useDropshippingResource(() => api.getProduct(productId), [api, productId]);
  const product = resource.data?.product;

  const [shopIds, setShopIds] = React.useState(undefined);
  const [mode, setMode] = React.useState("auto");
  const [marginPercent, setMarginPercent] = React.useState(40);
  const [exchangeRate, setExchangeRate] = React.useState(1);
  const [filterUnavailableVariants, setFilterUnavailableVariants] = React.useState(true);
  const [titleOverride, setTitleOverride] = React.useState("");
  const [descriptionOverride, setDescriptionOverride] = React.useState("");
  const [priceOverride, setPriceOverride] = React.useState("");
  const [tagsInput, setTagsInput] = React.useState("");
  const [selectedImages, setSelectedImages] = React.useState([]);
  // ONE global variant selection ("Select all" = every variant CJ has for
  // this product, from the complete variant list -- not only the cached rows).
  const [variantSelection, setVariantSelection] = React.useState({ mode: "all" });
  const variantsResource = useDropshippingResource(() => api.getProductVariants(productId), [api, productId]);
  const allVariants = variantsResource.data?.variants?.length ? variantsResource.data.variants : (product?.variants || []);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!product) return;
    setTitleOverride(product.title || "");
    setDescriptionOverride(product.description || "");
    setSelectedImages(product.images || []);
  }, [product]);

  const toggleImage = (url) => {
    setSelectedImages((prev) => prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]);
  };


  const autoPreviewPrice = product
    ? (Number(product.costPrice || 0) * Number(exchangeRate || 1) * (1 + Number(marginPercent || 0) / 100)).toFixed(2)
    : "0.00";

  const handleImport = async () => {
    setBusy(true);
    setError("");
    try {
      const payload = {
        boutiqueId: shopIds,
        mode,
        marginPercent: Number(marginPercent) || 0,
        exchangeRate: Number(exchangeRate) || 1,
        filterUnavailableVariants,
        selectedImageUrls: selectedImages,
        variantSelection: variantSelectionPayload(variantSelection, allVariants),
        ...(mode === "manual" ? {
          titleOverride,
          descriptionOverride,
          priceOverride: priceOverride ? Number(priceOverride) : undefined,
          tagsOverride: tagsInput.split(",").map((tag) => tag.trim()).filter(Boolean),
        } : {}),
      };
      const result = await api.importCJProduct(product.externalProductId, payload);
      navigate(`/dropshipping/products/${productId}`, { state: { imported: true } });
      return result;
    } catch (mutationError) {
      setError(mutationError.message);
    } finally {
      setBusy(false);
    }
  };

  return h(React.Fragment, null,
    h(PageHeader, {
      title: t("h_cj_wizard_title", "Import to Elakay"),
      action: h(LinkButton, { to: `/dropshipping/products/${productId}`, variant: "secondary" }, t("h_cj_back_products", "Back to product")),
    }),
    error ? h("div", { className: "cjds-alert", role: "alert" }, error) : null,
    h(ResourceState, { loading: resource.loading, error: resource.error, empty: !product, onRetry: resource.reload, t },
      product ? h("section", { className: "cjds-card" }, h("div", { className: "cjds-card-body" },

        h("div", { className: "cjds-row" },
          h(Button, { variant: mode === "auto" ? "" : "secondary", onClick: () => setMode("auto") }, t("h_cj_wizard_auto", "Automatic")),
          h(Button, { variant: mode === "manual" ? "" : "secondary", onClick: () => setMode("manual") }, t("h_cj_wizard_manual", "Manual"))
        ),

        h("h4", null, t("h_cj_wizard_pricing", "Pricing")),
        h("label", null, t("h_cj_wizard_margin", "Margin (%)"),
          h("input", { type: "number", value: marginPercent, onChange: (e) => setMarginPercent(e.target.value) })),
        h("label", null, t("h_cj_wizard_exchange_rate", "Exchange rate (from USD)"),
          h("input", { type: "number", step: "0.01", value: exchangeRate, onChange: (e) => setExchangeRate(e.target.value) })),
        h("p", { className: "cjds-muted" }, `${t("h_cj_wizard_preview_price", "Estimated sale price")}: $${mode === "auto" ? autoPreviewPrice : (priceOverride || autoPreviewPrice)}`),

        mode === "manual" ? h(React.Fragment, null,
          h("h4", null, t("h_cj_wizard_content", "Content")),
          h("label", null, t("h_cj_wizard_title_field", "Title"),
            h("input", { type: "text", value: titleOverride, onChange: (e) => setTitleOverride(e.target.value) })),
          h("label", null, t("h_cj_wizard_description_field", "Description"),
            h("textarea", { value: descriptionOverride, onChange: (e) => setDescriptionOverride(e.target.value) })),
          h("label", null, t("h_cj_wizard_price_field", "Sale price override"),
            h("input", { type: "number", step: "0.01", value: priceOverride, onChange: (e) => setPriceOverride(e.target.value) })),
          h("label", null, t("h_cj_wizard_tags", "Tags (comma separated)"),
            h("input", { type: "text", value: tagsInput, onChange: (e) => setTagsInput(e.target.value) }))
        ) : null,

        h("h4", null, t("h_cj_wizard_images", "Images")),
        h("div", { className: "cjds-row" },
          (product.images || []).map((url) => h("label", { key: url, className: "cjds-image-pick" },
            h("input", { type: "checkbox", checked: selectedImages.includes(url), onChange: () => toggleImage(url) }),
            h("img", { src: url, alt: "", width: 80, height: 80 })
          ))
        ),

        h("h4", null, t("h_cj_wizard_variants", "Variants")),
        h("label", null,
          h("input", { type: "checkbox", checked: filterUnavailableVariants, onChange: (e) => setFilterUnavailableVariants(e.target.checked) }),
          t("h_cj_wizard_filter_unavailable", "Automatically exclude out-of-stock variants")),
        h(ProductShopSelector, { value: shopIds, onChange: setShopIds }),
        h(CJVariantMultiSelector, {
          variants: allVariants,
          selection: variantSelection,
          onChange: setVariantSelection,
          t,
          loading: variantsResource.loading,
          error: variantsResource.error && !allVariants.length ? String(variantsResource.error?.message || variantsResource.error) : "",
          onRetry: variantsResource.reload,
        }),

        h("div", { className: "cjds-row" },
          h(Button, { disabled: busy || (allVariants.length > 0 && selectedVariantIds(variantSelection, allVariants).length === 0), onClick: handleImport }, busy ? t("h_cj_wizard_importing", "Importing...") : t("h_cj_wizard_import", "Import"))
        )
      )) : null
    )
  );
};

module.exports = { DropshippingImportWizard };
